import { HOMEBASED_LEAD_DAYS } from '../../constants';
import {
  CreateNewIcon,
  RadioActiveIcon,
  RadioInactiveIcon,
} from '@/assets/svg';
import { checkIsSpecialLocations, renderUnitNumber } from '@/helpers';
import ModalAddOrEditAddress from '@/module/all-users/component/ModalAddOrEditAddress';
import patientDispatcher from '@/module/patient/actions';
import locationDispatcher from '@/module/setup/action';
import { formatTimeShowUILocal } from '@/module/setup/utils';
import CustomDate from '@/new-components/CustomDate';
import CustomRadioButton from '@/new-components/CustomRadioButton';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import InputLabel from '@material-ui/core/InputLabel';
import Radio from '@material-ui/core/Radio';
import Typography from '@material-ui/core/Typography';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import AccessTimeRoundedIcon from '@material-ui/icons/AccessTimeRounded';
import CloseIcon from '@material-ui/icons/Close';
import { isEmpty, isEqual } from 'lodash';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';

const TYPE_MODAL = { Create: 'create', Edit: 'edit' };
const STEP = { DateTime: 0, Address: 1 };

const titleStyles = (theme) => ({
  root: { margin: 0, padding: theme.spacing(2) },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: 20,
    color: theme.palette.grey[500],
  },
  text: { fontWeight: 600 },
});

const useStyles = makeStyles((theme) => ({
  content: { height: 460, overflowY: 'auto' },
  label: { color: '#666666', fontWeight: 600, marginBottom: 8, marginTop: 16 },
  txtQuota: { fontSize: 16, color: '#666666' },
  emptyWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: '24px 0',
  },
  emptyIcon: {
    width: 88,
    height: 88,
    borderRadius: '50%',
    background: '#F1F1F1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
    '& svg': { fontSize: 40, color: '#B0B0B0' },
  },
  emptyTitle: { fontSize: 18, fontWeight: 700, color: '#333333' },
  emptySub: { fontSize: 16, color: '#666666', marginTop: 4 },
  chip: {
    display: 'inline-block',
    background: '#FFE9FB',
    color: '#AD5E99',
    fontSize: 12,
    fontWeight: 600,
    borderRadius: 12,
    padding: '2px 12px',
    marginBottom: 8,
  },
  addressText: { fontSize: 16, color: '#333333' },
  addNew: { textTransform: 'unset', fontWeight: 600, marginTop: 8 },
}));

const CustomDialogTitle = withStyles(titleStyles)((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <DialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6" className={classes.text}>
        {children}
      </Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
});

const CustomDialog = withStyles({
  paper: { borderRadius: 16, width: 640, maxWidth: '90vw' },
})(Dialog);

const CustomButton = withStyles({
  root: {
    minWidth: 120,
    height: 40,
    borderRadius: 8,
    fontSize: 16,
    fontWeight: 400,
    textTransform: 'unset',
  },
  containedPrimary: { boxShadow: 'none' },
})(Button);

const AddressCard = withStyles({
  root: {
    padding: 16,
    marginBottom: 12,
    boxShadow: 'none',
    borderRadius: 12,
    border: '1px solid #E0E0E0',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    '&.selected': { borderColor: '#AD5E99' },
  },
})(Card);

// Mirrors the timeslot validity rules used in SelectPlaceAndTimeStep.
const checkIsPastDate = (value) =>
  moment(value).startOf('date').unix() < moment().startOf('date').unix();
const checkIsPastTime = (endTime) => moment(endTime).unix() < moment().unix();
const checkInvalidTimeslot = (row) => {
  const { startDate, endDate, isActive } = row;
  if (checkIsPastDate(startDate)) return true;
  if (checkIsPastTime(endDate)) return true;
  if (!isActive) return true;
  return false;
};

const formatAddressText = (a) =>
  [
    a?.address,
    isEmpty(a?.floor) && isEmpty(a?.unitNumber)
      ? null
      : `#${renderUnitNumber(a?.floor, a?.unitNumber)}`,
    a?.postalCode ? `Singapore ${a.postalCode}` : null,
  ]
    .filter(Boolean)
    .join(', ');

/**
 * Homebased blood taking ($0) for eligible EHS packages. Two steps:
 *  0) Select Date & Time  — a new date + slot that overrides the EHS slot.
 *  1) Select Address      — pick a saved user address or add a new one.
 * Returns { addressId, date, timeSlotId } via onConfirm.
 */
const HomebasedBloodTakingModal = ({ open, onClose, onConfirm, patientId }) => {
  const classes = useStyles();
  const [step, setStep] = useState(STEP.DateTime);

  // Step 0 - date & time
  const [date, setDate] = useState(null);
  const [timeSlot, setTimeSlot] = useState([]);
  const [timeSlotChecked, setTimeSlotChecked] = useState('');
  const [loadingSlot, setLoadingSlot] = useState(false);
  const [homebasedLocationId, setHomebasedLocationId] = useState('');

  // Step 1 - address
  const [listAddress, setListAddress] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState({});
  const [openAddrModal, setOpenAddrModal] = useState(false);
  const [typeModal, setTypeModal] = useState(TYPE_MODAL.Create);
  const [editingAddress, setEditingAddress] = useState({});

  const minDate = useMemo(
    () => moment().add(HOMEBASED_LEAD_DAYS, 'days').startOf('day'),
    []
  );

  // Resolve the homebased location: fetch the personal HBS health-screenings,
  // then pick the homebased (special) location and keep its locationId.
  const fetchHomebasedLocation = () => {
    locationDispatcher.getHomebasedLocations((result = []) => {
      const homebased =
        result.find((l) =>
          checkIsSpecialLocations(l?.locationName || l?.name)
        ) || result[0];
      setHomebasedLocationId(homebased?.locationId || homebased?.id || '');
    });
  };

  const fetchAddresses = (autoSelectId) => {
    if (!patientId) return;
    patientDispatcher.getUserAddress(patientId, (result = []) => {
      setListAddress(result);
      if (autoSelectId) {
        const found = result.find((it) => it.id === autoSelectId);
        if (found) setSelectedAddress(found);
      } else {
        setSelectedAddress((prev) =>
          prev?.id ? prev : result.find((it) => it.isDefault) || {}
        );
      }
    });
  };

  // Reset when opened; preload saved addresses.
  useEffect(() => {
    if (open) {
      setStep(STEP.DateTime);
      setDate(null);
      setTimeSlot([]);
      setTimeSlotChecked('');
      setSelectedAddress({});
      setHomebasedLocationId('');
      fetchHomebasedLocation();
      fetchAddresses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, patientId]);

  const onGetDataTimeSlot = (value) => {
    if (!value || !homebasedLocationId) return;
    setLoadingSlot(true);
    locationDispatcher.getHomebasedTimeSlot(
      {
        locationId: homebasedLocationId,
        date: moment(value).format('YYYY-MM-DD'),
      },
      (result) => {
        const dayOfWeek = moment(value).day();
        const slots = (result || []).filter(
          (it) => it.dayOfWeek === dayOfWeek && !checkInvalidTimeslot(it)
        );
        setTimeSlot(slots);
        setLoadingSlot(false);
      }
    );
  };

  const handleChangeDate = (value) => {
    setDate(value);
    setTimeSlotChecked('');
    onGetDataTimeSlot(value);
  };

  const handleSubmitAddress = (data) => {
    if (typeModal === TYPE_MODAL.Create) {
      const oldIds = listAddress.map((it) => it.id);
      patientDispatcher.createUserAddress(data, () => {
        setOpenAddrModal(false);
        // createUserAddress does not return the new id -> re-fetch and pick the
        // address whose id was not present before (the just-created one).
        patientDispatcher.getUserAddress(patientId, (result = []) => {
          setListAddress(result);
          const created =
            result.find((it) => !oldIds.includes(it.id)) ||
            result.find((it) => it.isDefault);
          if (created) setSelectedAddress(created);
        });
      });
    } else {
      patientDispatcher.editUserAddress(data, () => {
        setOpenAddrModal(false);
        fetchAddresses(editingAddress?.id);
      });
    }
  };

  const isDateTimeValid = !!date && !!timeSlotChecked;
  const isAddressValid = !!selectedAddress?.id;

  const handleConfirm = () => {
    if (!isAddressValid) return;
    onConfirm({
      addressId: selectedAddress.id,
      locationId: homebasedLocationId,
      date,
      timeSlotId: timeSlotChecked,
    });
  };

  const title =
    step === STEP.DateTime
      ? 'Choose your blood-taking appointment'
      : 'Choose your blood-taking location';

  return (
    <>
      <CustomDialog
        scroll="body"
        disableEnforceFocus
        open={open}
        onClose={onClose}
        disableBackdropClick
      >
        <CustomDialogTitle onClose={onClose}>{title}</CustomDialogTitle>

        <DialogContent dividers className={classes.content}>
          {step === STEP.DateTime ? (
            <>
              <InputLabel className={classes.label}>Select date</InputLabel>
              <CustomDate
                selectedDate={date}
                minDate={minDate}
                isFullWidth
                placeholder="DD/MM/YYYY"
                clearable={false}
                onChange={handleChangeDate}
                style={{ height: 48 }}
              />

              {date && (
                <>
                  <InputLabel className={classes.label}>Select time</InputLabel>
                  <Grid container spacing={2}>
                    {timeSlot.map((item) => (
                      <Grid key={`${item.id}`} item lg={4} md={6} xs={12}>
                        <CustomRadioButton
                          keyValue="timeslotId"
                          item={item}
                          onClick={() => setTimeSlotChecked(item.id)}
                          checked={item.id === timeSlotChecked}
                        >
                          {item?.startDate &&
                            formatTimeShowUILocal(item.startDate)}
                          <span className={classes.txtQuota}>
                            {item.quotaUsed}/{item.quotaBase}
                          </span>
                        </CustomRadioButton>
                      </Grid>
                    ))}
                    {!loadingSlot && isEmpty(timeSlot) && (
                      <Grid item xs={12}>
                        <div className={classes.emptyWrap}>
                          <div className={classes.emptyIcon}>
                            <AccessTimeRoundedIcon />
                          </div>
                          <div className={classes.emptyTitle}>
                            No time slots available
                          </div>
                          <div className={classes.emptySub}>
                            Please select another date.
                          </div>
                        </div>
                      </Grid>
                    )}
                  </Grid>
                </>
              )}
            </>
          ) : (
            <>
              {listAddress.map((it) => (
                <AddressCard
                  key={it.id}
                  variant="outlined"
                  className={
                    isEqual(selectedAddress?.id, it?.id) ? 'selected' : ''
                  }
                  onClick={() => setSelectedAddress(it)}
                >
                  <Box display="flex" alignItems="center">
                    <Radio
                      checked={isEqual(selectedAddress?.id, it?.id)}
                      color="primary"
                      icon={<RadioInactiveIcon />}
                      checkedIcon={<RadioActiveIcon />}
                    />
                    <Box>
                      {it.isDefault && (
                        <span className={classes.chip}>
                          Main delivery address
                        </span>
                      )}
                      <Typography className={classes.addressText}>
                        {formatAddressText(it)}
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    color="primary"
                    style={{ minWidth: 55, textTransform: 'unset' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingAddress(it);
                      setTypeModal(TYPE_MODAL.Edit);
                      setOpenAddrModal(true);
                    }}
                  >
                    Edit
                  </Button>
                </AddressCard>
              ))}

              <Button
                className={classes.addNew}
                color="primary"
                startIcon={<CreateNewIcon />}
                onClick={() => {
                  setEditingAddress({});
                  setTypeModal(TYPE_MODAL.Create);
                  setOpenAddrModal(true);
                }}
              >
                Add new address
              </Button>
            </>
          )}
        </DialogContent>

        <DialogActions style={{ padding: 16 }}>
          {step === STEP.DateTime ? (
            <>
              <CustomButton
                variant="outlined"
                color="primary"
                onClick={onClose}
              >
                Cancel
              </CustomButton>
              <CustomButton
                variant="contained"
                color="primary"
                disabled={!isDateTimeValid}
                onClick={() => setStep(STEP.Address)}
              >
                Next
              </CustomButton>
            </>
          ) : (
            <>
              <CustomButton
                variant="outlined"
                color="primary"
                onClick={() => setStep(STEP.DateTime)}
              >
                Back
              </CustomButton>
              <CustomButton
                variant="contained"
                color="primary"
                disabled={!isAddressValid}
                onClick={handleConfirm}
              >
                Confirm
              </CustomButton>
            </>
          )}
        </DialogActions>
      </CustomDialog>

      {openAddrModal && (
        <ModalAddOrEditAddress
          open={openAddrModal}
          parentId={patientId}
          selectedAddress={editingAddress}
          typeModal={typeModal}
          onClose={() => setOpenAddrModal(false)}
          onSubmit={handleSubmitAddress}
        />
      )}
    </>
  );
};

export default HomebasedBloodTakingModal;
